import * as React from "react"
import * as TooltipPrimitive from "@radix-ui/react-tooltip"
import { cn } from "@/lib/utils"

function TooltipProvider({
  delayDuration = 0,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Provider>) {
  return (
    <TooltipPrimitive.Provider
      data-slot="tooltip-provider"
      delayDuration={delayDuration}
      {...props}
    />
  )
}

function Tooltip({
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Root>) {
  return (
    <TooltipProvider>
      <TooltipPrimitive.Root data-slot="tooltip" {...props} />
    </TooltipProvider>
  )
}

function TooltipTrigger({
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Trigger>) {
  return <TooltipPrimitive.Trigger data-slot="tooltip-trigger" {...props} />
}

function TooltipContent({
  className,
  sideOffset = 0,
  children,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Content>) {
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        data-slot="tooltip-content"
        sideOffset={sideOffset}
        className={cn(
          "bg-card-reverse text-card-reverse-foreground border-border animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 w-fit origin-(--radix-tooltip-content-transform-origin) rounded-md px-4 py-4 text-xs max-w-md",
          className
        )}
        {...props}
      >
        {children}
        <TooltipPrimitive.Arrow className="bg-card-reverse fill-card-reverse z-50 size-2.5 translate-y-[calc(-50%_-_2px)] rotate-45 rounded-[2px]" />
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  )
}

export const TooltipFormula = ({
  children,
  className,
  label = "Formula",
}: {
  children: React.ReactNode;
  className?: string;
  label?: string;
}) => {
  return (
    <div className={cn("space-y-1 mt-4", className)}>
      <div className="text-[10px] font-medium">{label}</div>
      <div className="text-xs bg-tooltip-formula-background text-tooltip-formula p-1.5 rounded-sm">
        {children}
      </div>
    </div>
  )
}

export const TooltipNoora = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  return (
    <div className={cn("flex gap-2 mt-4", className)}>
      <svg className="shrink-0 mt-1" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="7.86133" cy="8" r="8" fill="white" />
        <g filter="url(#filter0_f_4640_8482)">
          <ellipse cx="10.2683" cy="6.71552" rx="2.71552" ry="2.71552" fill="#DFFF32" />
        </g>
        <g filter="url(#filter1_f_4640_8482)">
          <circle cx="6.44575" cy="8.98481" r="4.01509" fill="#C4B9FE" />
        </g>
        <defs>
          <filter id="filter0_f_4640_8482" x="4.60452" y="1.05178" width="11.3271" height="11.3271" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
            <feGaussianBlur stdDeviation="1.47411" result="effect1_foregroundBlur_4640_8482" />
          </filter>
          <filter id="filter1_f_4640_8482" x="-4.44851" y="-1.90945" width="21.7886" height="21.7886" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
            <feGaussianBlur stdDeviation="3.43959" result="effect1_foregroundBlur_4640_8482" />
          </filter>
        </defs>
      </svg>

      <div className="text-xs text-tooltip-formula italic">
        {children}
      </div>
    </div>
  )
}

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider }
